import { Button } from "@/components/ui/baseUi/button"
import { CalendarInput } from "@/components/ui/forms/calendarInput"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { displayedFullDateFormat } from "@/modules/utils/dateUtils"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { ArrowLeft, CalendarDays } from "lucide-react"
import { type Dispatch, type SetStateAction } from "react"
import * as Yup from "yup"

export type MemberSendOfferModalEndDateProps = {
  deferredDate: Date | null
  endDate: Date | null
  setEndDate: Dispatch<SetStateAction<Date | null>>
  setIsEndDateVisible: Dispatch<SetStateAction<boolean>>
  className?: string
} & React.ComponentProps<"div">

const endDateYupValidation = (deferredDate: Date | null) =>
  Yup.object({
    endDate: Yup.date()
      .nullable()
      .min(
        deferredDate ? dayjs(deferredDate).toDate() : dayjs().toDate(),
        deferredDate
          ? word("member.send.offer.modal.endDate.errorForm.biggerThanStartDate")
          : word("member.send.offer.modal.endDate.errorForm.biggerThanToday"),
      ),
  })

export function MemberSendOfferModalEndDate({
  deferredDate,
  endDate,
  setEndDate,
  setIsEndDateVisible,
  className,
  ...props
}: MemberSendOfferModalEndDateProps) {
  const formik = useFormik<{ endDate: Date | null }>({
    initialValues: { endDate },
    validationSchema: endDateYupValidation(deferredDate),
    onSubmit: () => {
      try {
        setEndDate(formik.values.endDate)
        setIsEndDateVisible(false)
      } catch (e: any) {
        console.error(LoggerType.UI + "end date in send box offer", e)
      }
    },
  })

  return (
    <div className={cn("mt-6 flex w-full grow flex-col gap-6", className)} {...props}>
      {/* TITLE */}
      <div className="flex items-center justify-center gap-2">
        <CalendarDays color="currentColor" strokeWidth={1} size={13} className="text-textDefault-body" />
        <Text fontStyle="TS" className="text-center text-textDefault-body">
          {word("member.send.offer.modal.endDate.title")}
        </Text>
      </div>

      <CalendarInput
        label={word("member.send.offer.modal.endDate.titleInput")}
        name="endDate"
        getFieldProps={formik.getFieldProps}
        error={getFormErrorMessage("endDate", formik)}
        setFieldValue={formik.setFieldValue}
        placeholder={endDate ? dayjs(endDate).format("L") : dayjs().format("L")}
        calendarProps={{
          minDate: deferredDate ? dayjs(deferredDate).toDate() : dayjs().startOf("day").toDate(),
          maxDate: dayjs().add(1, "year").toDate(),
          dateFormat: displayedFullDateFormat,
        }}
      />

      <div className="flex grow flex-col items-center justify-center gap-6">
        <HUCardAlert
          description={word("member.send.offer.modal.endDate.information.period")}
          icon={<WarningHexagonalIcon color="currentColor" className="h-4 min-h-4 w-4 min-w-4 text-primary-700" />}
          className="flex flex-row items-center gap-2 rounded-2xl py-4"
          descriptionFontStyle="BM"
          style={{ whiteSpace: "break-spaces" }}
        />
        <HUCardAlert
          description={word("member.send.offer.modal.endDate.information.commitment")}
          icon={<WarningHexagonalIcon color="currentColor" className="h-4 min-h-4 w-4 min-w-4 text-primary-700" />}
          className="flex flex-row items-center gap-2 rounded-2xl py-4"
          descriptionFontStyle="BM"
          style={{ whiteSpace: "break-spaces" }}
        />
      </div>

      {/* BUTTON */}
      <div className="mt-2 flex w-full flex-row justify-center gap-2">
        <Button
          variant="outline"
          size="lg"
          className="grow"
          onClick={() => {
            setIsEndDateVisible(false)
            setEndDate(null)
          }}
        >
          <ArrowLeft strokeWidth={2} color="currentColor" size={15} />
          {endDate !== null ? word("global.reset") : word("global.cancel")}
        </Button>
        <Button variant="primary" size="lg" className="grow" onClick={() => formik.handleSubmit()}>
          {word("global.check")}
        </Button>
      </div>
    </div>
  )
}
